import { createEffect, onCleanup } from "solid-js";
import { RoomHub } from "../../Multidevice/RoomHub";
import { SessionState } from "../../types/SessionState";

type HostStatus = "listening" | "paused" | "ended";

/**
 * Keeps a SignalR hub connected (multi-device mode) and publishes host status transitions.
 * This is purely a side-effect hook
 */
export function useHostStatusHub(input: {
  sessionId: () => string | null;
  captureMode: () => "mic" | "multi" | "file";
  sessionState: () => SessionState;
  workflowStatus: () => "draft" | "in_progress" | "completed";
}) {
  let hub: RoomHub | null = null;
  let lastSentStatus: HostStatus | null = null;

  const disconnect = async () => {
    try {
      await hub?.disconnect();
    } catch (e) {
      console.warn("[hostStatusHub] disconnect failed:", e);
    } finally {
      hub = null;
      lastSentStatus = null;
    }
  };

  // Manage hub connection lifecycle.
  createEffect(() => {
    const id = input.sessionId();
    const mode = input.captureMode();
    const s = input.sessionState();

    const shouldConnect =
      Boolean(id) &&
      mode === "multi" &&
      (s === SessionState.Listening || s === SessionState.Connected);

    if (!shouldConnect) {
      disconnect();
      return;
    }

    if (!hub) hub = new RoomHub();
    hub
      .connect()
      .then(() => (id ? hub?.join(id) : undefined))
      .catch((e) => console.warn("[hostStatusHub] connect/join failed:", e));
  });

  // Publish host status transitions.
  createEffect(() => {
    const id = input.sessionId();
    if (!id) return;
    if (input.captureMode() !== "multi") return;
    if (!hub) return;

    const s = input.sessionState();
    const workflow = input.workflowStatus();

    let next: HostStatus | null = null;
    if (s === SessionState.Listening || s === SessionState.Connected)
      next = "listening";
    else if (s === SessionState.Paused) next = "paused";
    else if (s === SessionState.Idle && workflow === "completed")
      next = "ended";

    if (!next || next === lastSentStatus) return;
    lastSentStatus = next;
    hub
      .sendHostStatus({ sessionId: id, status: next })
      .catch((e) => console.warn("[hostStatusHub] sendHostStatus failed:", e));
  });

  onCleanup(() => {
    disconnect();
  });
}
